<template>
  <div class="task-panel" :class="{ 'task-panel-collapsed': collapsed }">
    <!-- 头部：收起 AI 审查助手（点击后由父组件把整个面板收成 0 宽） -->
    <div class="assistant-head">
      <button
        class="assistant-toggle"
        title="收起AI审查助手"
        @click="emit('collapse')"
      >
        <SvgIcon name="pack-up" :size="15" />
        <span>收起AI审查助手</span>
      </button>
    </div>

    <!-- 任务列表：始终渲染 stages，按 step-by-step 推进；下方接问答区 -->
    <div ref="taskListRef" class="task-list">
      <div
        v-for="(stage, idx) in stageItems"
        :key="idx"
        class="step"
        :class="`step-${stageStateOf(idx)}`"
      >
        <div class="step-dot">
          <template v-if="stageStateOf(idx) === 'done'">✓</template>
          <span
            v-else-if="stageStateOf(idx) === 'processing'"
            class="processing-dots"
          >
            <i></i><i></i><i></i>
          </span>
        </div>
        <div>
          <div class="step-title">{{ stage.name }}</div>
          <div v-if="stage.desc" class="step-desc">{{ stage.desc }}</div>
        </div>
      </div>
      <div v-if="!stageItems.length" class="step-empty">加载审查节点…</div>

      <!-- 问答区（流式渲染） -->
      <div v-if="qaList.length" class="qa-section">
        <div v-for="(qa, i) in qaList" :key="i" class="qa-item">
          <div class="qa-question">{{ qa.question }}</div>
          <div class="qa-answer" :class="{ 'qa-error': qa.error }">
            <template v-if="qa.answer">{{ qa.answer }}</template>
            <span v-else-if="qa.streaming" class="qa-typing">
              <i></i><i></i><i></i>
            </span>
            <span v-else-if="qa.error">回答失败，请重试</span>
          </div>
        </div>
      </div>

      <!-- 滚动锚点：流式渲染时让最新内容始终可见 -->
      <div ref="bottomAnchorEl" class="bottom-anchor" aria-hidden="true"></div>
    </div>

    <!-- 底部输入 -->
    <div class="task-footer">
      <a-textarea
        v-model:value="questionInput"
        :rows="2"
        placeholder="请输入您关于合同想了解的其它问题"
        class="question-input"
        @pressEnter="handleSend"
      />
      <div class="send-row">
        <span class="send-hint">Enter 发送</span>
        <a-button
          size="small"
          type="primary"
          :disabled="status === 'reviewing' || isAsking"
          @click="handleSend"
          >{{ isAsking ? "回答中…" : "发送" }}</a-button
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { message } from "ant-design-vue";
import { askQuestion } from "@/api/contractReview";

const props = defineProps({
  // 'reviewing' | 'completed'
  status: { type: String, default: "reviewing" },
  // 审查节点列表（来自 getGetReviewStages）
  stages: { type: Array, default: () => [] },
  // 整个面板是否处于收起态（由父组件 ReviewResult 控制）
  collapsed: { type: Boolean, default: false },
  // 审查/合同记录 ID，作为 /api/ContractReview/Ask 的入参
  id: { type: [String, Number], default: null },
  // 单步推进间隔（ms）：历史查看用原速 1200，发起 / 重审用 10000
  stepMs: { type: Number, default: 1200 },
});

const emit = defineEmits(["collapse"]);

/* 把后端 stages 归一化为 [{ name, desc, raw }]：兼容 string / object
 * desc 字段命名兜底：常见英文字段 → 中文键 → 任意非标题字符串字段 */
const TITLE_KEYS = ["name", "title", "stageName", "label", "stepName"];
const DESC_KEYS = [
  "desc",
  "description",
  "content",
  "detail",
  "remark",
  "summary",
  "text",
  "info",
  "message",
  "stepDesc",
  "stepDescription",
];
const NON_DESC_KEYS = new Set([
  ...TITLE_KEYS,
  "step",
  "status",
  "id",
  "key",
  "code",
  "type",
]);

const pickDescFallback = (obj) => {
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "string" && v && !NON_DESC_KEYS.has(k)) return v;
  }
  return "";
};

const stageItems = computed(() => {
  const list = props.stages || [];
  if (list.length && typeof list[0] === "object") {
    console.log("[TaskPanel] raw stage[0]:", list[0]);
  }
  return list.map((s, i) => {
    let name = "";
    let desc = "";
    if (typeof s === "string") {
      name = s;
    } else if (s && typeof s === "object") {
      name = TITLE_KEYS.reduce((acc, k) => acc || s[k] || "", "");
      desc = DESC_KEYS.reduce((acc, k) => acc || s[k] || "", "");
      if (!desc) desc = pickDescFallback(s);
    }
    return { name: name || `步骤 ${i + 1}`, desc, raw: s };
  });
});

/* ============ step-by-step 推进逻辑 ============
 * currentStep 含义：进行中的步骤下标；
 *   i < currentStep → done
 *   i === currentStep → processing（除非 currentStep 已到 total，则全 done）
 *   i > currentStep → pending
 *
 * 节奏：
 *   - 审查中（status === 'reviewing'）：按 props.stepMs 逐步推进，到倒数第二步停住
 *     （历史查看 1200ms / 发起重审 10000ms，由父组件通过 stepMs 决定）
 *   - 审查完成（status === 'completed'）：直接把 currentStep 推到 total，
 *     所有步骤瞬间变 done，不再做动画 —— getOrStartReview 一返回就要全部收尾。
 */
const currentStep = ref(0);
let advanceTimer = null;

const clearAdvanceTimer = () => {
  if (advanceTimer) {
    clearTimeout(advanceTimer);
    advanceTimer = null;
  }
};

const scheduleAdvance = () => {
  clearAdvanceTimer();
  const total = stageItems.value.length;
  if (total === 0) return;
  // 已经推进到最后一步且审查未完成 → 停住等 completed
  if (currentStep.value === total - 1 && props.status !== "completed") {
    return;
  }
  if (currentStep.value >= total) return;
  advanceTimer = setTimeout(() => {
    currentStep.value += 1;
    scheduleAdvance();
  }, props.stepMs);
};

/* stages 变化（首次到达 / 切换审查记录）→ 重置并按当前 status 选节奏 */
watch(
  () => props.stages,
  (newStages) => {
    clearAdvanceTimer();
    currentStep.value = 0;
    if (!newStages || !newStages.length) return;
    // 审查已完成（历史查看 / 极速返回）→ 所有 step 立即完成，不做动画
    if (props.status === "completed") {
      currentStep.value = newStages.length;
      return;
    }
    scheduleAdvance();
  },
  { immediate: true },
);

/* status 翻到 completed → 所有 step 立即标为 done，不再做动画
 * （getOrStartReview 一返回就把全部步骤收尾，避免视觉上还在转圈） */
watch(
  () => props.status,
  (s) => {
    if (s === "completed") {
      clearAdvanceTimer();
      const total = stageItems.value.length;
      if (total > 0) currentStep.value = total;
    }
  },
);

onBeforeUnmount(() => {
  clearAdvanceTimer();
  // 卸载时主动取消未完成的问答流，避免 promise 挂在已销毁的组件上更新 state
  if (askAbortCtrl) askAbortCtrl.abort();
});

const stageStateOf = (idx) => {
  const total = stageItems.value.length;
  if (currentStep.value >= total) return "done";
  if (currentStep.value > idx) return "done";
  if (currentStep.value === idx) return "processing";
  return "pending";
};

/* ============ 底部输入 + 流式问答 ============
 * askQuestion 是 fetch + ReadableStream 实现的流式接口，每读到一段文本就回调 onChunk，
 * 这里把 chunk 累加到 qaList 最后一项的 answer 上，Vue 的深度响应式会驱动 UI 实时增长。
 *
 * 状态门禁：
 *   - status === 'reviewing' → 审查未完成，按钮已 disabled，handleSend 进不来
 *   - isAsking === true      → 上一条还在流，防重入
 *   - props.id == null       → 审查记录 ID 缺失，弹提示
 */
const questionInput = ref("");
const taskListRef = ref(null);
const bottomAnchorEl = ref(null);
const qaList = ref([]); // [{ question, answer, streaming, error }]
const isAsking = ref(false);
let askAbortCtrl = null;

/* 滚动到底部：用锚点 scrollIntoView 比直接设 scrollTop 更稳——
   流式 chunk 到达后 DOM 高度可能在 nextTick 之前还在变化，
   scrollIntoView 由浏览器保证在当前布局完成后触发；
   requestAnimationFrame 再包一层确保 paint 阶段已应用最新高度。 */
const scrollToBottom = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      bottomAnchorEl.value?.scrollIntoView({ block: "end" });
    });
  });
};

const handleSend = async () => {
  const q = questionInput.value.trim();
  if (!q || isAsking.value) return;
  if (props.id == null) {
    message.warning("审查记录 ID 缺失，无法提问");
    return;
  }
  questionInput.value = "";

  // 先把问题挂上去（streaming=true），用户立刻看到自己的问题 + 思考中占位
  qaList.value.push({ question: q, answer: "", streaming: true, error: false });
  // 关键：通过 index 走代理访问 item，确保 Vue 3 的 SET trap 在每次 chunk 时触发响应式更新；
  // 直接持有 push 前的裸对象引用去改 .answer，可能不走代理、不触发渲染。
  const itemIdx = qaList.value.length - 1;
  scrollToBottom();

  isAsking.value = true;
  askAbortCtrl = new AbortController();
  try {
    await askQuestion({
      id: props.id,
      question: q,
      signal: askAbortCtrl.signal,
      onChunk: (chunk) => {
        // 通过代理访问，触发响应式
        qaList.value[itemIdx].answer += chunk;
        scrollToBottom();
      },
    });
    qaList.value[itemIdx].streaming = false;
  } catch (err) {
    if (err?.name === "AbortError") {
      // 用户切走/卸载主动取消：不报错，保留已收到的部分
      qaList.value[itemIdx].streaming = false;
    } else {
      console.error("[TaskPanel] askQuestion error:", err);
      qaList.value[itemIdx].error = true;
      qaList.value[itemIdx].streaming = false;
      message.error("问答失败，请重试");
    }
  } finally {
    isAsking.value = false;
    askAbortCtrl = null;
  }
};
</script>

<style lang="less" scoped>
.task-panel {
  width: 292px;
  min-width: 220px;
  flex-shrink: 0;
  background: white;
  border-right: 1px solid #e3eaf4;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease, min-width 0.25s ease, opacity 0.2s ease,
    border-color 0.25s ease;
  overflow: hidden;
}

/* 收起态：列宽收到 0、整体淡出、不再响应点击；
   空间由 .main-grid 的 flex 自然回收给中间列；
   “展开”入口由 ReviewResult 在 DocPanel 顶部另放一个按钮提供。 */
.task-panel-collapsed {
  width: 0;
  min-width: 0;
  opacity: 0;
  pointer-events: none;
  border-right-color: transparent;
}

/* 头部：收起 / 展开 按钮 */
.assistant-head {
  padding: 8px 14px;
  border-bottom: 1px solid #edf1f7;
  flex-shrink: 0;
}
.assistant-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  // border: 1px solid #e1e8f2;
  border: none;
  border-radius: 4px;
  background: #fff;
  color: #41546d;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  &:hover {
    border-color: #1768ff;
    color: #1768ff;
    background: #f5f8ff;
  }
}

/* 任务列表 */
.task-list {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px 8px;
}

/* 单个步骤：卡片，dot + (标题 + 描述) */
.step {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 9px;
  padding: 11px;
  border: 1px solid #e6ecf5;
  border-radius: 12px;
  margin-bottom: 9px;
  background: #fff;
}

/* 圆点 */
.step-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: #f5f7fa;
  border: 1px solid #d4dae5;
  color: transparent;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}
.step-done .step-dot {
  background: #1aa97b;
  border-color: #1aa97b;
  color: #fff;
}
.step-processing .step-dot {
  background: #1768ff;
  border-color: #1768ff;
  color: transparent;
}
.step-pending .step-dot {
  background: #f5f7fa;
  border-color: #d4dae5;
  color: transparent;
}

/* 进行中：3 个白色小圆点 */
.processing-dots {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.processing-dots i {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #fff;
  display: inline-block;
  animation: processing-dots-blink 1.4s infinite ease-in-out both;
}
.processing-dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.processing-dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes processing-dots-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 标题 + 描述 */
.step-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: #203452;
  word-break: break-all;
}
.step-processing .step-title {
  color: #1768ff;
}
.step-pending .step-title {
  color: #a4adc0;
  font-weight: 400;
}
.step-desc {
  font-size: 12px;
  color: #65748d;
  line-height: 1.55;
  margin-top: 3px;
  word-break: break-all;
}
.step-pending .step-desc {
  color: #b8bfcc;
}

.step-empty {
  color: #a4adc0;
  font-size: 13px;
  text-align: center;
  padding: 24px 0;
}

/* ============ 问答区（流式） ============
 * 问题气泡靠右、答案气泡靠左，跟常见 IM 一致。
 * 答案 white-space: pre-wrap 保留后端推过来的换行；
 * 流式期间靠 .qa-typing 的 3 个闪点占位，首 chunk 到达后由 qa.answer 顶替。 */
.qa-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #e6ecf5;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.qa-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.qa-question {
  align-self: flex-end;
  max-width: 88%;
  padding: 7px 10px;
  border-radius: 10px 10px 2px 10px;
  background: #edf4ff;
  color: #33445f;
  font-size: 12px;
  line-height: 1.55;
  word-break: break-word;
}
.qa-answer {
  align-self: flex-start;
  max-width: 92%;
  padding: 8px 10px;
  border-radius: 10px 10px 10px 2px;
  border: 1px solid #e5ebf3;
  background: #fff;
  // color: #94a0b1;
  font-size: 12px;
  line-height: 1.6;
  word-break: break-word;
  white-space: pre-wrap;
}
.qa-answer.qa-error {
  background: #fff1f0;
  color: #d83232;
}
/* 思考中的打字占位：3 个小蓝点闪动 */
.qa-typing {
  display: inline-flex;
  gap: 3px;
  align-items: center;
  height: 14px;
}
.qa-typing i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #1768ff;
  display: inline-block;
  animation: qa-typing-blink 1.4s infinite ease-in-out both;
}
.qa-typing i:nth-child(2) {
  animation-delay: 0.2s;
}
.qa-typing i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes qa-typing-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.85);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* 滚动锚点：零尺寸，只用作 scrollIntoView 的目标 */
.bottom-anchor {
  height: 0;
  margin: 0;
  padding: 0;
}

/* 底部输入 */
.task-footer {
  border-top: 1px solid #edf1f7;
  padding: 14px 16px 12px;
  flex-shrink: 0;
}
.send-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}
.send-hint {
  font-size: 12px;
  color: #8b97aa;
}
</style>

<style lang="less">
/* question-input 跨组件样式（非 scoped） */
.question-input {
  border-radius: 10px !important;
  resize: none !important;
}
</style>
